import {
  Component, OnDestroy, ViewChild,
  ChangeDetectorRef, Output, EventEmitter, ElementRef,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Observable } from 'rxjs';
import * as _ from 'lodash';

import { BaseModalComponent } from 'shared/modal/_base-modal.component';
import { createFormControl } from 'utils/form-control';
import { ReportsService } from 'shared/reports/reports.service';
import { BrandService } from 'shared/brand/brand.service';
import { ModalV2Component } from 'shared/modal/modal-v2/modal-v2.component';

import { ReportFiltersComponent} from './report-filters/report-filters.component';
import {takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'configure-report-modal',
  templateUrl: 'configure-report-modal.component.html',
  styleUrls: ['configure-report-modal.component.scss'],
})
export class ConfigureReportModalComponent implements OnDestroy {
  form: UntypedFormGroup;
  ghostValue: string = 'reports-modal';
  isActive: boolean = true;
  showLoader: boolean = false;

  // options
  reportTypes: Array<any> = [];

  @ViewChild(ModalV2Component, {static: false}) modal: ModalV2Component | void;
  @ViewChild(ReportFiltersComponent, {static: false}) reportFilters: ReportFiltersComponent | void;
  @ViewChild('reportFilters', {static: false}) reportFiltersEl: ElementRef | void;

  @Output() configureFinished: EventEmitter<any> = new EventEmitter();

  constructor(
    private fb: UntypedFormBuilder,
    private reportsService: ReportsService,
    private brandService: BrandService,
    private ref: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      reportType: createFormControl('reportType', ''),
    });
  }
  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get showActionPanel(): boolean {
    if (this.reportFilters) {
      // report type, part type, make are selected
      const partTypes = this.reportFilters.getSelectedPartTypes();
      const vcdbFilters = this.reportFilters.getVcdbFilter();

      return (
        _.get(this.form, 'dirty') &&
        partTypes.length &&
        vcdbFilters.make.length &&
        vcdbFilters.minYear
      );
    }
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnDestroy() {
    this.isActive = false;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

   public show() {
     this.reportsService.getTypes(this.brandId)
       .pipe(takeWhile(() => this.isActive))
       .subscribe((res) => {
         if (Array.isArray(res)) {
           this.reportTypes = res;
           this.ref.markForCheck();
         }
       });
    if (this.modal instanceof ModalV2Component) {
      this.modal.show();
    }
   }

   public hide() {
     if (this.modal instanceof ModalV2Component) {
       this.modal.hide();
     }
   }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onGenerateReportClick() {
    if (!this.reportFilters) {
      return;
    }
    const partTypes = this.reportFilters.getSelectedPartTypes();
    const vcdbFilters = this.reportFilters.getVcdbFilter();

    const vcdbGroup = {
      make: (vcdbFilters.make.length === 1) ? vcdbFilters.make[0] : vcdbFilters.make,
      minYear: vcdbFilters.minYear,
      maxYear: vcdbFilters.maxYear,
    };

    const vcdbAttrGroupFilters = this.reportFilters.getVcdbAttributesFilter();
    const vcdbAttrGroup = {};

    _.forEach(vcdbAttrGroupFilters, (group) => {
      const attrs = _.filter(group.fields, 'selected');
      _.forEach(attrs, attr => {
        const selectedOptions = _.map(_.filter(attr.optionsList, 'selected'), 'option');
        if (selectedOptions.length) {
          vcdbAttrGroup[attr.attribute_name] = selectedOptions.length === 1 ? selectedOptions[0] : selectedOptions;
        }
      });
    });

    const filter = {
      state: 'working',
      partType: (partTypes.length === 1) ? partTypes[0] : partTypes,
      groups: [
        {...vcdbGroup, ...vcdbAttrGroup},
      ]
    };

    this.reportsService.postReport(
      this.brandId,
      this.form.get('reportType').value,
      filter,
    )
      .subscribe((res) => {
        if (res.success) {
          this.configureFinished.emit();
        }
      });
  }

  onLoadingChange(loading: boolean) {
    this.setShowLoader(loading);
  }

  onAttrOptionsFetch() {
    if (!this.reportFiltersEl || !this.modal) {
      return;
    }
    const height = this.reportFiltersEl.nativeElement.offsetHeight;
    const position = this.reportFiltersEl.nativeElement.offsetTop;
    this.modal.scrollTo(position + height);
  }

  onModalHidden() {
    this.form.get('reportType').setValue(null);
    this.form.markAsPristine();
    if (this.reportFilters instanceof ReportFiltersComponent) {
      this.reportFilters.resetData();
    }
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private setShowLoader(showLoader: boolean) {
    this.showLoader = showLoader;
    this.ref.markForCheck();
  }
}
